<script lang="ts" setup>
import type { AsyncComponentLoader } from 'vue';

import { computed, defineAsyncComponent, ref, useAttrs } from '#imports';

type IconsImportMap = {
    [key: string]: () => Promise<{ default: string }>;
};

interface Props {
    size?: number;
    icon?: string;
}

const icons = import.meta.glob('@/assets/images/svg/**/*.svg') as IconsImportMap;

const iconImportFunctions: IconsImportMap = Object.keys(icons).reduce<IconsImportMap>(
    (acc, path) => {
        const parts = path.split('/');
        const fileName = parts[parts.length - 1];
        if (!fileName) return acc;
        const iconName = fileName.replace(
            '.svg',
            ''
        );

        if (icons[path]) acc[iconName] = icons[path];
        return acc;
    },
    {}
);

const props = defineProps<Props>();

defineOptions({
    inheritAttrs: false
});

const iconImport = computed(() => props.icon ? iconImportFunctions[props.icon] : iconImportFunctions['missing']);

const isError = ref(false);

const Icon = computed(() => defineAsyncComponent({
    loader: iconImport.value as AsyncComponentLoader<Component>,
    onError: () => isError.value = true
}));
const attrs = useAttrs();

</script>

<template>
  <svg v-if="isError"
       :width="props.size"
       :height="props.size"
       v-bind="attrs"
       viewBox="0 0 16 16"
       xmlns="http://www.w3.org/2000/svg"
       fill="currentColor"
  >
    <path d="M4 1C2.36 1 1 2.36 1 4v1h1V4c0-1.1.9-2 2-2h1V1zm2 0v1h4V1zm5 0v1h1a2 2 0 0 1 2 2v1h1V4c0-1.64-1.36-3-3-3zM6 5a1 1 0 0 0 0 2 1 1 0 0 0 0-2zM1 6v4h1V6zm13 0v4h1V6zM9.5 8l-2 2L6 9l-2 2v.5c0 .5.5.5.5.5h7s.47-.04.5-.5v-1zM1 11v1c0 1.64 1.36 3 3 3h1v-1H4a2 2 0 0 1-2-2v-1zm13 0v1a2 2 0 0 1-2 2h-1v1h1c1.64 0 3-1.36 3-3v-1zm-8 3v1h4v-1zm0 0" />
  </svg>
  <component :is="Icon"
             v-else
             v-bind="attrs"
             :width="
               props.size"
             :height="props.size"
  />
</template>
